import { useEffect, useState, useRef, useCallback } from 'preact/hooks';
import { currentScreen } from '../../App';
import { sfx } from '../../lib/sfx';
import { music } from '../../lib/music';
import { translate } from '../../lib/i18n';
import type { Lang } from '../../lib/i18n';
import { useGameSocket } from '../../hooks/useWebSocket';
import type { ServerMessage, SuggestedAction, MusicalMood, CharacterSheet, Outcome } from '@mxltiverse/shared';

interface NpcDialogue {
  npc_id: string;
  name: string;
  dialogue: string;
  action?: string;
}

interface GameState {
  narrationBuffer: string;
  displayedText: string;
  suggestedActions: SuggestedAction[];
  musicalMood: MusicalMood;
  character: CharacterSheet | null;
  turn: number;
  turnBudget: number;
  processing: boolean;
  narrationDone: boolean;
  diceResult: { stat: string; roll: number; difficulty: number; total: number; outcome: Outcome } | null;
  npcDialogues: NpcDialogue[];
  gameOver: { reason: string; canRetry: boolean } | null;
  error: string | null;
}

const TYPE_SPEED = 15; // ms per word

export function GameScreen({ saveId, wsUrl }: { saveId: string; wsUrl: string }) {
  const [state, setState] = useState<GameState>({
    narrationBuffer: '',
    displayedText: '',
    suggestedActions: [],
    musicalMood: 'exploration',
    character: null,
    turn: 0,
    turnBudget: 0,
    processing: true,
    narrationDone: false,
    diceResult: null,
    npcDialogues: [],
    gameOver: null,
    error: null,
  });

  const [inputText, setInputText] = useState('');
  const [musicMuted, setMusicMuted] = useState(false);
  const lang: Lang = 'es'; // ponytail: game language comes from state, defaulted for now
  const t = (k: Parameters<typeof translate>[0]) => translate(k, lang);
  const narrationRef = useRef<HTMLDivElement>(null);
  const typeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Play music based on mood
  useEffect(() => {
    if (state.musicalMood) {
      music.play(state.musicalMood);
    }
  }, [state.musicalMood]);

  // Stop music on unmount
  useEffect(() => {
    return () => music.stop();
  }, []);

  // ====== Typewriter effect ======
  useEffect(() => {
    const buf = state.narrationBuffer;
    const shown = state.displayedText;

    if (buf.length <= shown.length) return;

    // Find next word boundary
    const remaining = buf.slice(shown.length);
    const wordMatch = remaining.match(/^(\s*\S+)/);
    if (!wordMatch) return;

    const nextChunk = wordMatch[1];
    const nextText = shown + nextChunk;

    // Play sound every 2-3 words to avoid noise
    const wordCount = nextText.split(/\s+/).length;
    if (wordCount % 2 === 0) sfx.key();

    typeTimerRef.current = setTimeout(() => {
      setState((s) => ({ ...s, displayedText: nextText }));

      // Check if we've displayed everything
      if (nextText.length >= buf.length && !state.processing) {
        setState((s) => ({ ...s, narrationDone: true }));
      }
    }, TYPE_SPEED);

    return () => {
      if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
    };
  }, [state.narrationBuffer, state.displayedText, state.processing]);

  const handleWsMessage = useCallback((msg: ServerMessage) => {
    switch (msg.type) {
      case 'connected':
        sfx.success();
        setState((s) => ({
          ...s,
          narrationBuffer: msg.payload.narration || '',
          displayedText: '',
          narrationDone: !msg.payload.narration,
          suggestedActions: msg.payload.suggested_actions ?? [],
          musicalMood: msg.payload.musical_mood ?? 'exploration',
          character: msg.payload.character,
          turn: msg.payload.turn,
          turnBudget: msg.payload.turn_budget,
          processing: false,
        }));
        break;

      case 'turn_start':
        sfx.click();
        setState((s) => ({
          ...s, processing: true, diceResult: null,
          narrationBuffer: '', displayedText: '', narrationDone: false,
          npcDialogues: [],
        }));
        break;

      case 'dice_roll':
        sfx.error();
        setState((s) => ({ ...s, diceResult: msg.payload }));
        break;

      case 'master_streaming':
        setState((s) => ({ ...s, narrationBuffer: s.narrationBuffer + msg.payload.chunk }));
        break;

      case 'npc_dialogue':
        sfx.toggle();
        setState((s) => ({
          ...s,
          npcDialogues: [...s.npcDialogues, {
            npc_id: msg.payload.npc_id,
            name: msg.payload.name,
            dialogue: msg.payload.dialogue,
            action: msg.payload.action,
          }],
        }));
        break;

      case 'checkpoint_reached':
        sfx.verify();
        break;

      case 'turn_end':
        sfx.select();
        setState((s) => ({
          ...s,
          narrationBuffer: s.narrationBuffer || msg.payload.narration,
          suggestedActions: msg.payload.suggested_actions,
          musicalMood: msg.payload.musical_mood,
          character: s.character
            ? { ...s.character, hp: Math.max(0, s.character.hp + (msg.payload.state_changes.hp_delta ?? 0)) }
            : null,
          turn: s.turn + 1,
          processing: false,
        }));
        break;

      case 'game_over':
        sfx.error();
        setState((s) => ({ ...s, gameOver: { reason: msg.payload.reason, canRetry: msg.payload.can_retry }, processing: false, narrationDone: true }));
        break;

      case 'error':
        if (msg.payload.message !== 'Partida guardada.') {
          setState((s) => ({ ...s, error: msg.payload.message, processing: false }));
        }
        break;
    }
  }, []);

  const { connected, send } = useGameSocket({ url: wsUrl, onMessage: handleWsMessage });

  // Auto-scroll narration
  useEffect(() => {
    if (narrationRef.current) {
      narrationRef.current.scrollTop = narrationRef.current.scrollHeight;
    }
  }, [state.displayedText]);

  function handleAction(actionText: string, source: 'suggestion' | 'free_text') {
    if (state.processing) return;
    sfx.click();
    setInputText('');
    setState((s) => ({ ...s, processing: true, narrationBuffer: '', displayedText: '', narrationDone: false }));
    send({ type: 'action', payload: { text: actionText, source } });
  }

  function handleSuggestion(index: number) {
    if (state.processing) return;
    sfx.click();
    setState((s) => ({ ...s, processing: true, narrationBuffer: '', displayedText: '', narrationDone: false }));
    send({ type: 'select_suggestion', payload: { index } });
  }

  function handleSave() {
    sfx.click();
    send({ type: 'save' });
  }

  function toggleMusic() {
    sfx.click();
    const muted = music.toggleMute();
    setMusicMuted(muted);
  }

  function skipTypewriter() {
    if (typeTimerRef.current) clearTimeout(typeTimerRef.current);
    setState((s) => ({ ...s, displayedText: s.narrationBuffer, narrationDone: !s.processing }));
  }

  const hpPct = state.character ? (state.character.hp / state.character.max_hp) * 100 : 0;
  const turnPct = state.turnBudget > 0 ? (state.turn / state.turnBudget) * 100 : 0;
  const showActions = !state.processing && state.narrationDone && state.suggestedActions.length > 0
    && state.displayedText.length >= state.narrationBuffer.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-dark)' }}>
      {/* ====== Top bar (slim, full width) ====== */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0.4rem 1rem',
        borderBottom: '3px solid var(--border-color)',
        background: 'var(--bg-panel)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span class="pixel-title text-yellow" style={{ fontSize: '14px' }}>MXLTIVERSE</span>
          <span class="text-dim" style={{ fontSize: '15px' }}>{state.character?.name ?? '???'}</span>
          {/* HP inline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span class="text-red" style={{ fontSize: '14px' }}>HP</span>
            <div style={{ width: '80px', height: '10px', background: 'var(--bg-dark)', border: '2px solid var(--border-color)' }}>
              <div style={{
                height: '100%', width: `${hpPct}%`,
                background: hpPct > 50 ? 'var(--accent-green)' : hpPct > 25 ? 'var(--accent-yellow)' : 'var(--accent-red)',
                transition: 'width 0.3s',
              }} />
            </div>
            <span style={{ fontSize: '14px' }}>{state.character?.hp ?? 0}/{state.character?.max_hp ?? 0}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
          <span class="text-dim" style={{ fontSize: '15px' }}>{t('turn')} {state.turn}/{state.turnBudget}</span>
            <button class="pixel-btn" onClick={toggleMusic} style={{ fontSize: '13px', padding: '0.2rem 0.7rem' }}>
              {musicMuted ? t('music_off') : t('music_on')}
            </button>
            <button class="pixel-btn" onClick={handleSave} style={{ fontSize: '13px', padding: '0.2rem 0.7rem' }}>{t('save')}</button>
            <button class="pixel-btn" onClick={() => { sfx.click(); currentScreen.value = 'start'; }} style={{ fontSize: '13px', padding: '0.2rem 0.7rem' }}>{t('exit')}</button>
        </div>
      </div>

      {/* ====== Narration area (full width) ====== */}
      <div
        ref={narrationRef}
        onClick={skipTypewriter}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '2rem calc((100vw - 720px) / 2)',
          fontSize: '22px',
          lineHeight: 1.5,
          cursor: state.displayedText.length < state.narrationBuffer.length ? 'pointer' : 'default',
        }}
      >
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          {state.displayedText}

          {/* Typewriter cursor */}
          {!state.narrationDone && state.displayedText && (
            <span class="cursor text-yellow" />
          )}

          {/* Processing indicator */}
          {state.processing && !state.displayedText && (
            <span class="cursor text-dim">...</span>
          )}

          {/* Dice result */}
          {state.diceResult && (
            <div class="mt-3" style={{
              display: 'inline-block', padding: '0.6rem 1.2rem',
              border: '3px solid ' + (state.diceResult.outcome.includes('success') ? 'var(--accent-green)' : 'var(--accent-red)'),
              background: 'var(--bg-panel)',
            }}>
              <span class="text-yellow">D20 </span>
              <span style={{ fontSize: '32px' }} class={
                state.diceResult.outcome === 'critical_success' ? 'text-green flicker'
                : state.diceResult.outcome === 'critical_failure' ? 'text-red flicker'
                : state.diceResult.outcome.includes('success') ? 'text-green'
                : state.diceResult.outcome === 'partial' ? 'text-yellow'
                : 'text-red'
              }>{state.diceResult.roll}</span>
              <span class="text-dim"> + {state.diceResult.total - state.diceResult.roll} = {state.diceResult.total} vs {state.diceResult.difficulty}</span>
              <span class={
                state.diceResult.outcome.includes('success') ? 'text-green'
                : state.diceResult.outcome === 'partial' ? 'text-yellow'
                : 'text-red'
              }> {state.diceResult.outcome.replace(/_/g, ' ')}</span>
            </div>
          )}

          {/* Click to skip hint — only while text is being revealed */}
          {state.displayedText.length < state.narrationBuffer.length && state.displayedText.length > 20 && (
            <p class="text-dim mt-3" style={{ fontSize: '14px' }}>{t('click_skip')}</p>
          )}

          {/* NPC dialogues */}
          {state.npcDialogues.length > 0 && state.narrationDone && (
            <div class="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {state.npcDialogues.map((d, i) => (
                <div key={i} style={{
                  borderLeft: '4px solid var(--accent-purple)',
                  padding: '0.5rem 0.8rem',
                  background: 'var(--bg-panel)',
                }}>
                  <span class="text-yellow" style={{ fontSize: '18px' }}>
                    {d.name}
                  </span>
                  <p style={{ fontSize: '20px', marginTop: '0.2rem' }}>"{d.dialogue}"</p>
                  {d.action && d.action !== 'nothing' && (
                    <p class="text-dim" style={{ fontSize: '16px', fontStyle: 'italic' }}>* {d.action}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ====== Action panel (only when narration done + not processing) ====== */}
      {showActions && (
        <div style={{
          flexShrink: 0,
          borderTop: '3px solid var(--border-color)',
          background: 'var(--bg-panel)',
          padding: '0.75rem calc((100vw - 720px) / 2)',
        }}>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            {/* Suggested actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.5rem' }}>
              {state.suggestedActions.map((action, i) => (
                <button
                  key={i}
                  class="pixel-btn"
                  onClick={() => handleSuggestion(i)}
                  onFocus={() => sfx.focus()}
                  style={{
                    fontSize: '17px', textAlign: 'left',
                    justifyContent: 'flex-start',
                    padding: '0.4rem 0.8rem',
                  }}
                >
                  <span class="text-yellow">{i + 1}.</span> {action.text}
                  {action.stat && action.difficulty && (
                    <span class="text-dim" style={{ fontSize: '14px', marginLeft: '0.5rem' }}>
                      [{action.stat.slice(0, 3).toUpperCase()} {action.difficulty}]
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Free text */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                class="pixel-input"
                type="text"
                placeholder={t('write_action')}
                value={inputText}
                onFocus={() => sfx.focus()}
                onInput={(e) => { sfx.key(); setInputText((e.target as HTMLInputElement).value); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && inputText.trim()) {
                    handleAction(inputText.trim(), 'free_text');
                  }
                }}
              />
              <button
                class="pixel-btn text-green"
                disabled={!inputText.trim()}
                onClick={() => handleAction(inputText.trim(), 'free_text')}
              >
                {t('act')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== Stats expandable (bottom-left floating) ====== */}
      {state.character && !showActions && !state.processing && (
        <div style={{
          position: 'fixed', bottom: '1rem', right: '1rem',
          background: 'var(--bg-panel)', border: '3px solid var(--border-color)',
          padding: '0.5rem 0.75rem', fontSize: '14px', zIndex: 50,
        }}>
          <div style={{ display: 'flex', gap: '0.8rem' }}>
            {Object.entries(state.character.stats).map(([k, v]) => (
              <span key={k}><span class="text-dim">{k.slice(0, 3)}</span> <span class={v >= 7 ? 'text-green' : v <= 3 ? 'text-red' : ''}>{v}</span></span>
            ))}
          </div>
        </div>
      )}

      {/* ====== Game Over overlay ====== */}
      {state.gameOver && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div class="pixel-border text-center" style={{ maxWidth: '500px', padding: '2rem' }}>
            <h2 class="pixel-title text-yellow mb-3" style={{ fontSize: '24px' }}>
              {state.gameOver.reason === 'defeat' ? t('defeat') : t('the_end')}
            </h2>
            <p class="text-dim mb-3">
              {state.gameOver.reason === 'defeat'
                ? t('hero_fell')
                : t('story_concluded')}
            </p>
            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center' }}>
              {state.gameOver.canRetry && (
                <button class="pixel-btn text-yellow" onClick={() => {
                  sfx.verify();
                  send({ type: 'load_checkpoint' });
                  setState((s) => ({ ...s, gameOver: null, processing: true, narrationBuffer: '', displayedText: '', narrationDone: false, npcDialogues: [] }));
                }}>
                  {t('retry')}
                </button>
              )}
                <button class="pixel-btn text-green" onClick={() => { sfx.click(); currentScreen.value = 'start'; }}>
                {t('home')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
